<template>
  <q-select
    ref="tagsSelector"
    :model-value="modelValue"
    options-dense
    :use-input="!preventInput"
    use-chips
    multiple
    emit-value
    input-debounce="0"
    :options="availableProgramTagsFiltered"
    :dense="dense"
    @update:model-value="onInputTags"
    @new-value="onAddProgramTag"
    @filter="onFilterProgramTags"
  >
    <template #selected>
      <q-chip
        v-if="((modelValue?.length ?? 0) === 0) && !noEmptyLabel"
        class="q-mr-xs"
        size="sm"
        color="primary"
        outline
        @remove="onInputTags([])"
      >
        {{ $t('tagSelect.labelAddTag') }}
      </q-chip>
      <template v-else>
        <q-chip
          v-for="(tag, index) in modelValue"
          :key="index"
          removable
          size="sm"
          class="q-mr-xs"
          :text-color="getTagTextColor(tag)"
          :style="getTagStyle(tag)"
          @remove="onInputTags(modelValue.filter((item) => item !== tag))"
        >
          {{ tag }}
        </q-chip>
      </template>
    </template>

    <template #no-option>
      <q-item>
        <q-item-section class="text-grey">
          {{ noOptionLabel }}
        </q-item-section>
      </q-item>
    </template>
  </q-select>
</template>
<script>
import {
  chain, difference, filter, find, get, includes, isEmpty, sortBy, toLower, trim,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useAuth } from 'src/composables/auth';
import Tags from 'src/mixins/Tags';
import { auth } from 'src/store/auth';
import { tags } from 'src/store/tags';

export default {
  name: 'TagSelect',

  mixins: [Tags],

  props: {
    modelValue: {
      type: Array,
      required: true,
    },

    dense: {
      type: Boolean,
      required: false,
      default: false,
    },

    preventInput: {
      type: Boolean,
      default: false,
    },

    limitTags: {
      type: Array,
      default: null,
    },

    noEmptyLabel: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  setup() {
    const { userHasPrivilege } = useAuth();

    return { userHasPrivilege };
  }, // setup()

  data() {
    return {
      availableProgramTagsFiltered: [],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient']),
    ...mapState(tags, ['unsavedClinicTags', 'availableTagsFormatted']),

    availableProgramTagsSorted() {
      return chain(this.availableTagsFormatted)
        .sortBy((val) => val.label.toLowerCase())
        .map('label')
        .value();
    }, // availableProgramTagsSorted()

    availableTagsLabels() {
      return chain(this.availableProgramTagsSorted)
        .concat(this.unsavedClinicTags)
        .filter((tag) => !this.limitTags || includes(this.limitTags, tag))
        .value();
    }, // availableTagsLabels()

    userCanAddProgramTags() {
      return this.userHasPrivilege('pages/tags');
    }, // userCanAddProgramTags()

    noOptionLabel() {
      return this.userCanAddProgramTags
        ? this.$t('tagSelect.labelNoMatchingTag')
        : this.$t('tagSelect.labelNoPrivilegeToAddTag');
    }, // noOptionLabel()

  }, // computed

  watch: {
    modelValue: {
      deep: true,
      handler(newValue) {
        this.$nextTick(() => {
          this.setUnsavedTags(difference(newValue, this.availableProgramTagsSorted));
        });
      },
    },
  }, // watch

  created() {
    if (isEmpty(this.availableTagsFormatted)) {
      this.reloadTags({ clinicId: get(this.userDetails, 'clinic', null) });
    }
  }, // created()

  methods: {
    ...mapActions(tags, ['reloadTags', 'setUnsavedTags']),

    onInputTags(value) {
      this.$emit('update:modelValue', sortBy(value, (val) => val.toLowerCase()));

      this.$refs.tagsSelector.updateInputValue('');
    },

    onAddProgramTag(val, done) {
      const trimmedVal = trim(val);
      const trimmedValLower = trimmedVal.toLowerCase();
      const trimmedFixedVal = trimmedVal.split('/').join('-');

      if (trimmedVal) {
        const existingValue = find(
          this.availableTagsFormatted,
          (programTag) => programTag.label.trim().toLowerCase() === trimmedValLower,
        );

        if (!existingValue && !this.userCanAddProgramTags) {
          return;
        }

        if (!existingValue) {
          this.setUnsavedTags([...this.unsavedClinicTags, trimmedFixedVal]);
        }

        done(existingValue || trimmedVal, 'add-unique');
      }
    }, // onAddProgramTag()

    onFilterProgramTags(val, update) {
      update(() => {
        if (val === '') {
          this.availableProgramTagsFiltered = this.availableTagsLabels;
        } else {
          const trimmedValLower = val.toLowerCase();
          this.availableProgramTagsFiltered = filter(
            this.availableTagsLabels,
            (v) => toLower(v).indexOf(trimmedValLower) > -1,
          );
        }
      });
    }, // onFilterProgramTags()

  },
};
</script>
